import { useQueueStore } from "@/stores/queue-store";
import { Button } from "@/components/ui/button";
import { Loader2, Clock } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { useWebSocket } from "@/providers/websocket-provider";

interface QueueStatusProps {
  onLeaveQueue?: () => void; // Make this optional
}

export function QueueStatus({ onLeaveQueue }: QueueStatusProps) {
  const { isInQueue } = useQueueStore();
  const webSocketService = useWebSocket();
  const [searchTime, setSearchTime] = useState(0); // Timer in seconds
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  
  // Start/stop the timer based on queue status
  useEffect(() => {
    // Start timer when entering queue
    if (isInQueue && !timerRef.current) {
      // Set the start time if not already set
      if (startTimeRef.current === null) {
        startTimeRef.current = Date.now();
      }
      
      // Create interval to update the timer
      timerRef.current = setInterval(() => {
        if (startTimeRef.current !== null) {
          const elapsedSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setSearchTime(elapsedSeconds);
        }
      }, 1000);
    } 
    // Stop timer when leaving queue
    else if (!isInQueue && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
      startTimeRef.current = null;
      setSearchTime(0);
    }
    
    // Cleanup on component unmount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isInQueue]);
  
  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  // Function to handle leaving queue
  const handleLeaveQueue = () => {
    if (onLeaveQueue) {
      // Use the prop if provided
      onLeaveQueue();
    } else {
      // Otherwise use the WebSocket service directly
      webSocketService.leaveQueue();
    }
    
    // Reset timer (although this will also happen via the effect)
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    startTimeRef.current = null;
    setSearchTime(0);
  };
  
  // Don't show if not in queue
  if (!isInQueue) {
    return null;
  }
  
  return (
    <div className="fixed bottom-24 left-1/2 transform -translate-x-1/2 z-50 animate-in fade-in duration-200">
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full shadow-lg bg-black/80 border border-primary/40">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-xs">Searching</span>
        <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-black/40 border border-white/10">
          <Clock className="h-3 w-3 text-white/70" />
          <span className="text-xs font-mono">{formatTime(searchTime)}</span>
        </div>
        <Button
          onClick={handleLeaveQueue}
          variant="default"
          size="sm"
          className="h-7 ml-1 px-2 text-xs"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}